
import { CircleCheck, CircleX, Info, TriangleAlert, X } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import { IconButton } from "./Button";
import { cn } from "./cn";
import { holdUntilExitEnds } from "./motion";
import styles from "./overlay.module.css";
import type { Tone } from "./status";

const ICONS = {
  neutral: Info,
  information: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  danger: CircleX,
} as const;

const TONE_CLASS: Record<Tone, string> = {
  neutral: styles.toastNeutral,
  success: styles.toastSuccess,
  warning: styles.toastWarning,
  danger: styles.toastDanger,
  information: styles.toastInformation,
};

const DEFAULT_DURATION_MS = 5000;
const MAX_VISIBLE = 3;

export type ToastInput = {
  tone?: Tone;
  title: string;
  description?: string;
  duration?: number;
};

type ToastEntry = ToastInput & { id: number; closing?: boolean };

type ToastContextValue = { toast: (input: ToastInput) => void };

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  return context ?? { toast: () => undefined };
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<ToastEntry[]>([]);
  const nextId = useRef(0);
  const scheduled = useRef(new Set<number>());
  const holds = useRef(new Map<number, () => void>());

  const dismiss = useCallback((id: number) => {
    setEntries((current) =>
      current.map((entry) => (entry.id === id ? { ...entry, closing: true } : entry)),
    );
  }, []);

  const toast = useCallback((input: ToastInput) => {
    nextId.current += 1;
    const entry: ToastEntry = { ...input, id: nextId.current };
    setEntries((current) => {
      const next = [...current, entry];
      const live = next.filter((item) => !item.closing);
      if (live.length <= MAX_VISIBLE) return next;
      const evicted = new Set(live.slice(0, live.length - MAX_VISIBLE).map((item) => item.id));
      return next.map((item) => (evicted.has(item.id) ? { ...item, closing: true } : item));
    });
  }, []);

  useEffect(() => {
    for (const entry of entries) {
      if (!entry.closing || scheduled.current.has(entry.id)) continue;
      const id = entry.id;
      const cancel = holdUntilExitEnds(() => {
        scheduled.current.delete(id);
        holds.current.delete(id);
        setEntries((current) => current.filter((item) => item.id !== id));
      });
      scheduled.current.add(id);
      holds.current.set(id, cancel);
    }
  }, [entries]);

  useEffect(() => {
    const pending = holds.current;
    const marked = scheduled.current;
    return () => {
      for (const cancel of pending.values()) cancel();
      pending.clear();
      marked.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className={styles.toastViewport}
        role="region"
        aria-label={t("ui.notifications")}
        aria-live="polite"
        aria-atomic="false"
      >
        {entries.map((entry) => (
          <ToastItem key={entry.id} entry={entry} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({
  entry,
  onDismiss,
}: {
  entry: ToastEntry;
  onDismiss: (id: number) => void;
}) {
  const { t } = useTranslation();
  const tone = entry.tone ?? "neutral";
  const Icon = ICONS[tone];
  const duration = entry.duration ?? (tone === "danger" ? 0 : DEFAULT_DURATION_MS);
  const closing = entry.closing === true;
  const id = entry.id;

  useEffect(() => {
    if (closing || duration <= 0) return;
    const timer = setTimeout(() => onDismiss(id), duration);
    return () => clearTimeout(timer);
  }, [closing, duration, id, onDismiss]);

  return (
    <div className={styles.toastSlot} data-closing={closing || undefined}>
      <div
        className={cn(styles.toast, TONE_CLASS[tone])}
        data-closing={closing || undefined}
        role={tone === "danger" ? "alert" : undefined}
      >
        <Icon className={styles.toastIcon} size={16} strokeWidth={1.75} aria-hidden="true" />
        <div className={styles.toastCopy}>
          <p className={styles.toastTitle}>{entry.title}</p>
          {entry.description ? (
            <p className={styles.toastDescription}>{entry.description}</p>
          ) : null}
        </div>
        <div className={styles.toastActions}>
          <IconButton
            label={t("common.close")}
            size="sm"
            showTooltip={false}
            icon={<X size={16} strokeWidth={1.75} aria-hidden="true" />}
            onClick={() => onDismiss(id)}
          />
        </div>
      </div>
    </div>
  );
}
